import api from '@disco/api/server'

import type { TrackPlaylist, TrackPlaylistArtwork } from 'types/search-page'
import type { TrackId } from 'types/track'

export type PlaylistTrackArtwork = TrackPlaylistArtwork
export type AppearsOnPlaylist = TrackPlaylist

// Fetched lazily on hover; cached for the session so re-hovering the same
// track (or reopening its card) doesn't refetch.
const cache = new Map<TrackId, AppearsOnPlaylist[]>()
const inFlight = new Map<TrackId, Promise<AppearsOnPlaylist[]>>()

export const loadTrackPlaylists = (
  trackId: TrackId,
): Promise<AppearsOnPlaylist[]> => {
  const cached = cache.get(trackId)
  if (cached) return Promise.resolve(cached)

  const existing = inFlight.get(trackId)
  if (existing) return existing

  const promise = api
    .getTrackPlaylists(trackId)
    .then((byTrack: Record<string, AppearsOnPlaylist[]>) => {
      const playlists = byTrack?.[String(trackId)] ?? []
      cache.set(trackId, playlists)
      inFlight.delete(trackId)
      return playlists
    })
    .catch(error => {
      inFlight.delete(trackId)
      throw error
    })

  inFlight.set(trackId, promise)
  return promise
}

export const getCachedTrackPlaylists = (
  trackId: TrackId,
): AppearsOnPlaylist[] | undefined => cache.get(trackId)

export const __resetPlaylistsCache = (): void => {
  cache.clear()
  inFlight.clear()
}
